import React, { useRef, type FC } from "react";

import { useIntl } from "react-intl";
import { useRecoilCallback, useRecoilValue } from "recoil";

import {
  Chip,
  ChipIconInfo,
  ChipIconTimes,
  ChipIconWrapper,
  FormElement,
  SELECT_VARIANT_WITH_CHECKBOX,
  Select,
} from "mailup-web-components";

import useSelectFilters from "Hooks/useSelectFilters/useSelectFilters";
import { changeCriterionCallback } from "Pages/RecipientsList/recoil/callbacks/filters";
import { modalFilterDetailsOpen } from "Pages/RecipientsList/recoil/callbacks/modals";
import { criterionFilterState } from "Pages/RecipientsList/recoil/state/filters";

interface InputFiltersProps {
  index: number;
}

interface SelectedItemProps {
  value: number;
  label: string;
  name: string;
  description: string;
  type:
    | "Anagraphic"
    | "Activity"
    | "Device"
    | "Geolocation"
    | "MultiFilter"
    | "MultiType"
    | "Engagement"
    | "Tracking";

  onClick: (value: number, e: React.MouseEvent) => void;
}

const SelectedItemComponent: FC<SelectedItemProps> = ({
  label,
  value,
  onClick,
  name,
  description,
  type,
}) => {
  const modalOpen = useRecoilCallback(modalFilterDetailsOpen, []);

  const handleClickInfo = (_e: React.MouseEvent): void => {
    void modalOpen({
      id: value,
      name,
      description,
      type,
    });
  };

  const handleClickRemove = (e: React.MouseEvent): void => {
    onClick(value, e);
  };

  return (
    <Chip label={label} value={value}>
      <ChipIconWrapper
        onClick={handleClickInfo}
        data-mu-test="select-filter__details"
      >
        <ChipIconInfo />
      </ChipIconWrapper>
      <ChipIconWrapper
        onClick={handleClickRemove}
        data-mu-test="select-filter__remove"
      >
        <ChipIconTimes />
      </ChipIconWrapper>
    </Chip>
  );
};

const InputFilters: FC<InputFiltersProps> = ({ index }) => {
  const intl = useIntl();
  const dirty = useRef(false);

  const { options, optionsGroups, loading } = useSelectFilters();

  const { values } = useRecoilValue(criterionFilterState(index));

  const changeCriterion = useRecoilCallback(changeCriterionCallback, []);

  const handleChange = (v: number[]): void => {
    dirty.current = true;

    void changeCriterion("values", v, index);
  };

  const showFeedback = values.length === 0 && dirty.current;

  const h1 = (_a: any, _bb: any): void => {};

  return (
    <FormElement
      variant={showFeedback ? "danger" : undefined}
      feedback={
        showFeedback
          ? intl.formatMessage({ id: "Load.MailupCommon.Required" })
          : undefined
      }
    >
      <Select
        variant={SELECT_VARIANT_WITH_CHECKBOX}
        multiple
        searchable
        value={values}
        options={options}
        optionGroups={optionsGroups}
        loading={loading}
        disabled={loading}
        onChange={handleChange}
        error={showFeedback}
        data-mu-test="select-filters"
        placeholder={intl.formatMessage({
          id: "Load.RecipientsList.SelectFilterPlaceholder",
        })}
        SelectedItemComponent={SelectedItemComponent}
      />
    </FormElement>
  );
};

export default InputFilters;
